import React, { useState } from 'react';

import { useMutation } from '@apollo/client';
import DateFnsUtils from '@date-io/date-fns';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Alert, Box, Button, Divider, Grid, MenuItem, TextField } from '@mui/material';
import frLocale from 'date-fns/locale/fr';
import dayjs from 'dayjs';
import { Formik } from 'formik';

import candidateGqlSchema from '../../../../../services/candidate/gqlSchema';
import { optionsCAPI, optionsOH } from '../../data/contracts';
import { candidateSchema } from '../../validators/contract';
import FakeProgress from './FakeProgress';

const CandidateCardContractForm = ({ contact }) => {
  const contactId = contact.id;
  const contactInfo = contact.contactsInfos.find(infos => infos.sources && infos.sources.source === 'SALESFORCE');

  const [options, defaultPackIndex] = (() => {
    if (contactInfo.company.code === 'OPTIMHOME') {
      return [optionsOH, 8];
    }

    const currentDate = new Date();
    const cutoffDate = new Date('2025-01-01');

    return currentDate >= cutoffDate ? [optionsCAPI.filter(option => option.value > 10), 11] : [optionsCAPI, 6];
  })();

  const initialValues = {
    dateOfBirth: contactInfo.birth_date || '',
    birthName: contactInfo.birth_name || '',
    placeOfBirth: contactInfo.birth_place || '',
    birthCountry: contactInfo.birth_country || '',
    nationality: contactInfo.nationality || '',
    address: contactInfo.address || '',
    zipCode: contactInfo.zipcode || '',
    city: contactInfo.city || '',
    fatherName: contactInfo.father_name || '',
    fatherFirstname: contactInfo.father_firstname || '',
    motherName: contactInfo.mother_name || '',
    motherFirstname: contactInfo.mother_firstname || '',
    trainingMonth: new Date(),
    offerChoice: defaultPackIndex || '',
  };

  if (contactInfo.company.code === 'OPTIMHOME') {
    initialValues.externalRsacCity = contactInfo.externalRsacCity || '';
    initialValues.externalRsacNumber = contactInfo.externalRsacNumber || '';
  }

  const [signContracts, { loading }] = useMutation(candidateGqlSchema.Mutation.signContracts);

  const [progressFinished, setProgressFinished] = useState(false);

  const onSubmit = async values => {
    const { data } = await signContracts({
      variables: {
        params: {
          companyCode: contactInfo.company.code,
          contactId: contactId,
          ...values,
          dateOfBirth: dayjs(values.dateOfBirth).format('YYYY-MM-DD'),
          trainingMonth:
            contactInfo.company.code === 'OPTIMHOME'
              ? dayjs(values.trainingMonth).format('MMMM YYYY')
              : dayjs(values.trainingMonth).format('DD/MM/YYYY'),
        },
      },
    });

    setProgressFinished(true);

    window.location.assign(data.signContracts.url);
  };

  const nl2br = text =>
    text.split('\n').map((item, key) => (
      <span key={key}>
        {item}
        <br />
      </span>
    ));

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={candidateSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={onSubmit}
    >
      {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <DatePicker
                label={'Date de naissance'}
                id={'dateOfBirth'}
                inputVariant={'outlined'}
                variant={'inline'}
                size={'small'}
                format={'dd/MM/yyyy'}
                fullWidth
                onChange={value => {
                  setFieldValue('dateOfBirth', value);
                }}
                autoOk={true}
                disableFuture={true}
                value={values.dateOfBirth}
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth}
              />
            </MuiPickersUtilsProvider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={'Nom de naissance'}
              id={'birthName'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.birthName}
              error={!!errors.birthName}
              helperText={errors.birthName}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={'Ville de naissance'}
              id={'placeOfBirth'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.placeOfBirth}
              error={!!errors.placeOfBirth}
              helperText={errors.placeOfBirth}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={'Pays de naissance'}
              id={'birthCountry'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.birthCountry}
              error={!!errors.birthCountry}
              helperText={errors.birthCountry}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={'Nationalité'}
              id={'nationality'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.nationality}
              error={!!errors.nationality}
              helperText={errors.nationality}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={'Adresse'}
              id={'address'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={'Code postal'}
              id={'zipCode'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.zipCode}
              error={!!errors.zipCode}
              helperText={errors.zipCode}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={'Ville'}
              id={'city'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.city}
              error={!!errors.city}
              helperText={errors.city}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ margin: '0.5rem 0' }} />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={'Nom du père'}
              id={'fatherName'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.fatherName}
              error={!!errors.fatherName}
              helperText={errors.fatherName}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={'Nom de la mère'}
              id={'motherName'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.motherName}
              error={!!errors.motherName}
              helperText={errors.motherName}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={'Prénom du père'}
              id={'fatherFirstname'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.fatherFirstname}
              error={!!errors.fatherFirstname}
              helperText={errors.fatherFirstname}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={'Prénom de la mère'}
              id={'motherFirstname'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.motherFirstname}
              error={!!errors.motherFirstname}
              helperText={errors.motherFirstname}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ margin: '0.5rem 0' }} />
          </Grid>

          <Grid item xs={12}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <DatePicker
                label={'Mois de formation'}
                id={'trainingMonth'}
                inputVariant={'outlined'}
                variant={'inline'}
                views={['month', 'year']}
                size={'small'}
                format={'MM/yyyy'}
                fullWidth
                onChange={value => {
                  setFieldValue('trainingMonth', value);
                }}
                autoOk={true}
                disablePast={true}
                value={values.trainingMonth}
                error={!!errors.trainingMonth}
                helperText={errors.trainingMonth}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          {contactInfo.company.code === 'OPTIMHOME' && contact.originCandidate === 'MULTI' ? (
            <>
              <Grid item xs={6}>
                <TextField
                  label={'Numéro RSAC'}
                  id={'externalRsacNumber'}
                  variant={'outlined'}
                  fullWidth
                  size={'small'}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.externalRsacNumber}
                  error={!!errors.externalRsacNumber}
                  helperText={errors.externalRsacNumber}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={'Ville de greffe'}
                  id={'externalRsacCity'}
                  variant={'outlined'}
                  fullWidth
                  size={'small'}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.externalRsacCity}
                  error={!!errors.externalRsacCity}
                  helperText={errors.externalRsacCity}
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <TextField
              label={'Contrat'}
              id={'offerChoice'}
              variant={'outlined'}
              fullWidth
              size={'small'}
              select
              onChange={handleChange('offerChoice')}
              onBlur={handleBlur('offerChoice')}
              value={values.offerChoice}
              error={!!errors.offerChoice}
              helperText={errors.offerChoice}
            >
              {options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.title}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {loading || progressFinished ? (
            <Grid item xs={12}>
              <FakeProgress finished={progressFinished} />
            </Grid>
          ) : (
            values.offerChoice && (
              <>
                <Grid item xs={12}>
                  {options.find(option => option.value === values.offerChoice).text && (
                    <Alert severity='info'>
                      {nl2br(options.find(option => option.value === values.offerChoice).text)}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant={'contained'} color={'primary'} onClick={handleSubmit}>
                      <FontAwesomeIcon icon={faPenToSquare} />
                      <Box component='span' sx={{ marginLeft: '5px' }}>
                        Choisir cette offre et signer le contrat
                      </Box>
                    </Button>
                  </Box>
                </Grid>
              </>
            )
          )}
        </Grid>
      )}
    </Formik>
  );
};

export default React.memo(CandidateCardContractForm);
